<template>
  <Teleport to="body">
    <Transition name="context-menu">
      <div
        v-if="visible"
        ref="menuRef"
        class="context-menu"
        :style="menuStyle"
        @click.stop
        @contextmenu.prevent
      >
        <!-- 菜单项 -->
        <div
          v-for="(item, index) in sortedItems"
          :key="item.key || index"
          class="context-menu-item"
          :class="{
            'context-menu-item--disabled': item.disabled,
            'context-menu-item--danger': item.danger,
            'context-menu-item--divider': item.type === 'divider'
          }"
          @click="handleClick(item)"
        >
          <!-- 图标 -->
          <span v-if="item.icon" class="context-menu-icon">
            <component :is="item.icon" />
          </span>
          <!-- 标签 -->
          <span class="context-menu-label">{{ item.label }}</span>
          <!-- 快捷键 -->
          <span v-if="item.shortcut" class="context-menu-shortcut">
            {{ item.shortcut }}
          </span>
          <!-- 子菜单箭头 -->
          <span v-if="item.children" class="context-menu-arrow">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
            </svg>
          </span>
        </div>

        <!-- 子菜单 -->
        <div
          v-if="activeSubmenu !== null && sortedItems[activeSubmenu]?.children"
          class="context-menu context-menu--sub"
          :style="getSubmenuStyle(sortedItems[activeSubmenu])"
        >
          <div
            v-for="(child, childIndex) in sortedItems[activeSubmenu].children"
            :key="child.key || childIndex"
            class="context-menu-item"
            :class="{
              'context-menu-item--disabled': child.disabled,
              'context-menu-item--danger': child.danger
            }"
            @click="handleClick(child)"
          >
            <span v-if="child.icon" class="context-menu-icon">
              <component :is="child.icon" />
            </span>
            <span class="context-menu-label">{{ child.label }}</span>
            <span v-if="child.shortcut" class="context-menu-shortcut">
              {{ child.shortcut }}
            </span>
          </div>
        </div>
      </div>
    </Transition>

    <!-- 点击外部关闭遮罩 -->
    <div
      v-if="visible"
      class="context-menu-backdrop"
      @click="close"
    />
  </Teleport>
</template>

<script>
import { ref, computed, watch, h } from 'vue'

export default {
  name: 'ContextMenu',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    items: {
      type: Array,
      default: () => []
    },
    x: {
      type: Number,
      default: 0
    },
    y: {
      type: Number,
      default: 0
    },
    event: {
      type: Object,
      default: null
    }
  },
  emits: ['select', 'close'],
  setup(props, { emit }) {
    const menuRef = ref(null)
    const activeSubmenu = ref(null)

    // 排序菜单项
    const sortedItems = computed(() => {
      return [...props.items].sort((a, b) => {
        if (a.priority === undefined) a.priority = 100
        if (b.priority === undefined) b.priority = 100
        return a.priority - b.priority
      })
    })

    // 菜单位置
    const menuStyle = computed(() => {
      const { x, y } = props
      let left = x
      let top = y

      if (menuRef.value) {
        const rect = menuRef.value.getBoundingClientRect()
        const maxLeft = window.innerWidth - rect.width - 10
        const maxTop = window.innerHeight - rect.height - 10

        left = Math.min(left, maxLeft)
        top = Math.min(top, maxTop)

        // 子菜单位置
        if (activeSubmenu.value !== null) {
          const submenu = document.querySelector('.context-menu--sub')
          if (submenu) {
            const subRect = submenu.getBoundingClientRect()
            if (left + rect.width + subRect.width > window.innerWidth) {
              left = x - subRect.width
            }
          }
        }
      }

      return {
        left: `${left}px`,
        top: `${top}px`
      }
    })

    // 获取子菜单位置
    function getSubmenuStyle(item) {
      if (!menuRef.value) return {}

      const rect = menuRef.value.getBoundingClientRect()
      const itemIndex = props.items.findIndex(i => i === item)
      const itemElement = menuRef.value.children[itemIndex]

      if (!itemElement) return {}

      const itemRect = itemElement.getBoundingClientRect()

      return {
        left: `${rect.width}px`,
        top: `${itemRect.top}px`
      }
    }

    // 处理菜单项点击
    function handleClick(item) {
      if (item.disabled) return

      if (item.children) {
        return
      }

      emit('select', item)
      close()
    }

    // 关闭菜单
    function close() {
      activeSubmenu.value = null
      emit('close')
    }

    // 处理键盘导航
    function handleKeydown(e) {
      if (!props.visible) return

      const items = sortedItems.value.filter(i => i.type !== 'divider')
      const currentIndex = items.findIndex(i => i === items[activeSubmenu.value])

      switch (e.key) {
        case 'ArrowDown': {
          e.preventDefault()
          const nextIndex = currentIndex < items.length - 1 ? currentIndex + 1 : 0
          if (items[nextIndex]?.children) {
            activeSubmenu.value = props.items.indexOf(items[nextIndex])
          } else {
            activeSubmenu.value = null
          }
          break
        }
        case 'ArrowUp': {
          e.preventDefault()
          const prevIndex = currentIndex > 0 ? currentIndex - 1 : items.length - 1
          if (items[prevIndex]?.children) {
            activeSubmenu.value = props.items.indexOf(items[prevIndex])
          } else {
            activeSubmenu.value = null
          }
          break
        }
        case 'Enter': {
          e.preventDefault()
          const currentItem = items[currentIndex]
          if (currentItem) {
            handleClick(currentItem)
          }
          break
        }
        case 'Escape':
          e.preventDefault()
          if (activeSubmenu.value !== null) {
            activeSubmenu.value = null
          } else {
            close()
          }
          break
      }
    }

    // 处理点击外部
    function handleBackdropClick(e) {
      if (e.target === e.currentTarget) {
        close()
      }
    }

    // 监听显示状态
    watch(() => props.visible, (visible) => {
      if (visible) {
        activeSubmenu.value = null
        document.addEventListener('keydown', handleKeydown)
        document.addEventListener('click', handleBackdropClick)
      } else {
        document.removeEventListener('keydown', handleKeydown)
        document.removeEventListener('click', handleBackdropClick)
      }
    })

    // 默认图标
    const icons = {
      add: () => h('svg', { class: 'w-4 h-4', fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
        h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M12 4v16m8-8H4' })
      ]),
      edit: () => h('svg', { class: 'w-4 h-4', fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
        h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z' })
      ]),
      delete: () => h('svg', { class: 'w-4 h-4', fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
        h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16' })
      ]),
      copy: () => h('svg', { class: 'w-4 h-4', fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
        h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z' })
      ]),
      reminder: () => h('svg', { class: 'w-4 h-4', fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
        h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9' })
      ])
    }

    return {
      menuRef,
      sortedItems,
      menuStyle,
      activeSubmenu,
      getSubmenuStyle,
      handleClick,
      close,
      icons
    }
  }
}
</script>

<style scoped>
.context-menu {
  position: fixed;
  min-width: 180px;
  max-width: 280px;
  padding: 4px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 38px rgba(0, 0, 0, 0.1), 0 2px 9px rgba(0, 0, 0, 0.06);
  border: 1px solid #e2e8f0;
  z-index: 9999;
  user-select: none;
}

.context-menu--sub {
  position: absolute;
  top: 0;
  left: 100%;
}

.context-menu-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
  font-size: 14px;
  color: #1e293b;
}

.context-menu-item:hover:not(.context-menu-item--disabled):not(.context-menu-item--divider) {
  background: #f1f5f9;
}

.context-menu-item--disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.context-menu-item--danger {
  color: #ef4444;
}

.context-menu-item--danger:hover:not(.context-menu-item--disabled) {
  background: #fef2f2;
}

.context-menu-item--divider {
  height: 1px;
  padding: 0;
  margin: 4px 0;
  background: #e2e8f0;
  cursor: default;
}

.context-menu-item--divider:hover {
  background: #e2e8f0;
}

.context-menu-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
}

.context-menu-item--danger .context-menu-icon {
  color: #ef4444;
}

.context-menu-label {
  flex: 1;
}

.context-menu-shortcut {
  font-size: 12px;
  color: #94a3b8;
}

.context-menu-arrow {
  display: flex;
  align-items: center;
  color: #94a3b8;
}

/* 过渡动画 */
.context-menu-enter-active,
.context-menu-leave-active {
  transition: all 0.15s ease;
}

.context-menu-enter-from,
.context-menu-leave-to {
  opacity: 0;
  transform: scale(0.95);
}

.context-menu-backdrop {
  position: fixed;
  inset: 0;
  z-index: 9998;
}
</style>
